import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import {
  ChevronLeft,
  User,
  Users,
  AlertCircle,
  Clock,
  CheckCircle,
  X,
  Download,
  Send,
  Mail,
} from "lucide-react";
import jsPDF from "jspdf";

const API_BASE_URL = "https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150";

// Utility function to format category names
function formatCategoryName(categoryName) {
  if (!categoryName) return 'Resource';
  return categoryName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Parse ISO duration to minutes
function parseDuration(duration) {
  if (!duration) return 15;
  const match = duration.match(/PT(\d+H)?(\d+M)?/);
  if (!match) return 15;
  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;
  return hours * 60 + minutes;
}

// Get current Hong Kong time
function getHongKongTime() {
  return new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta" })
  );
}

// Check if a time is in the past relative to HK time
// function isTimePast(dateString, timeString) {
//   const selectedDate = new Date(dateString);
//   const hkNow = getHongKongTime();

//   const selectedDateOnly = new Date(
//     selectedDate.getFullYear(),
//     selectedDate.getMonth(),
//     selectedDate.getDate()
//   );
//   const todayOnly = new Date(
//     hkNow.getFullYear(),
//     hkNow.getMonth(),
//     hkNow.getDate()
//   );

//   if (selectedDateOnly < todayOnly) return true;
//   if (selectedDateOnly > todayOnly) return false;

//   const [hours, minutes] = timeString.split(":").map(Number);
//   const timeInMinutes = hours * 60 + minutes;
//   const nowInMinutes = hkNow.getHours() * 60 + hkNow.getMinutes();

//   return timeInMinutes <= nowInMinutes;
// }

// Time zone change handler
function isTimePast(dateString, timeString) {
  const slotDateTime = new Date(`${dateString}T${timeString}:00`); // local browser time
  return slotDateTime <= new Date();
}

// Add minutes to HH:mm string (24h)
const addMinutesToTime = (timeString, minutes) => {
  if (!timeString) return '';
  const [hours, mins] = timeString.split(':').map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMins = totalMinutes % 60;
  return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
};

const getAccessToken = () => sessionStorage.getItem('access_token');

// Toast Notification Component
function Toast({ message, type = "success", onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === "success" ? "bg-[#79C485]" : "bg-[#db6969]";
  const Icon = type === "success" ? CheckCircle : AlertCircle;

  return (
    <div
      className={`fixed top-4 right-4 z-50 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-80`}
      style={{ animation: "slideInRight 0.3s ease-out" }}
    >
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button
        onClick={onClose}
        className="text-white hover:text-gray-200 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function BookingDetailsSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex items-start gap-6 mb-8">
        <div className="w-16 h-16 bg-gray-300 rounded-lg"></div>
        <div className="flex-1">
          <div className="h-6 bg-gray-300 rounded mb-2 w-3/4"></div>
          <div className="h-5 bg-gray-300 rounded w-1/2"></div>
        </div>
        <div className="w-20 h-4 bg-gray-300 rounded"></div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1 lg:col-span-2 space-y-6">
          <div className="h-4 bg-gray-300 rounded w-20"></div>
          <div className="h-20 bg-gray-300 rounded"></div>
          <div className="h-4 bg-gray-300 rounded w-16"></div>
          <div className="flex gap-3">
            <div className="h-10 bg-gray-300 rounded w-24"></div>
            <div className="h-10 bg-gray-300 rounded w-32"></div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-4 bg-gray-300 rounded w-24"></div>
          <div className="h-40 bg-gray-300 rounded"></div>
        </div>
      </div>
    </div>
  );
}

// Confirmation Modal Component
function ConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm", 
  cancelText = "Cancel", 
  isLoading = false,
  confirmColor = "#db6969" 
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        style={{ fontFamily: "Inter" }}
      >
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="mb-6">
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: "Inter" }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: confirmColor,
              fontFamily: "Inter"
            }}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userMap, setUserMap] = useState(new Map());
  const [customerEmail, setCustomerEmail] = useState("");

  const [serviceDetails, setServiceDetails] = useState(null);

  const [availabilityBookings, setAvailabilityBookings] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const [actionLoading, setActionLoading] = useState(null);

  const [toast, setToast] = useState(null);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReactivateConfirmedModal, setShowReactivateConfirmedModal] = useState(false);
  const [showReactivateTentativeModal, setShowReactivateTentativeModal] = useState(false);
  const [showMarkTentativeModal, setShowMarkTentativeModal] = useState(false);
  const [showConfirmBookingModal, setShowConfirmBookingModal] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  const [resourceSchedule, setResourceSchedule] = useState(null);

  // Editable booking fields
  const [editedStartTime, setEditedStartTime] = useState("");
  const [editedEndTime, setEditedEndTime] = useState("");
  const [editedDate, setEditedDate] = useState("");
  const [editedNotes, setEditedNotes] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Share booking state
  const [allUsers, setAllUsers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [isSendingShare, setIsSendingShare] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [customEmail, setCustomEmail] = useState("");



  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Keep 12-hour format for the PDF/display areas
      timeZone: "Asia/Jakarta",
    });
  };

  // NEW: Added the 24-hour formatter back in
  const formatTime24 = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Jakarta'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "Asia/Jakarta",
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Asia/Jakarta",
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return "N/A";
    const diff = new Date(end) - new Date(start);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
    }
    return `${minutes} min`;
  };

  const getStatus = (booking) => {
    if (booking.is_canceled) return "Cancelled";
    if (booking.is_temporary) return "Tentative";
    return "Confirmed";
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Confirmed":
        return { backgroundColor: "#79C485", color: "white" };
      case "Tentative":
        return { backgroundColor: "#EDB02D", color: "white" };
      case "Cancelled":
        return { backgroundColor: "#db6969", color: "white" };
      default:
        return { backgroundColor: "#EDB02D", color: "white" };
    }
  };

  // Check if booking is in the past
  const isBookingInPast = (booking) => {
    if (!booking || !booking.ends_at) return false;
    const now = new Date();
    const bookingEnd = new Date(booking.ends_at);
    return bookingEnd < now;
  };

  // Helper function to get the weekday name for schedule matching
  const getWeekdayName = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return dayNames[date.getDay()];
  };

  useEffect(()=>{
    if(booking){
      let customerId = booking.metadata?.customer_id;
      const response = fetch(`https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/customer/getEmail`,{
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({customerId: customerId})
      })
      const data = response.then(res => res.json())
      data.then(res => {
        if(res && res.email){
          setCustomerEmail(res.email);
        } else {
          setCustomerEmail("");
        }
    })
    }
    console.log("Customer email set to:", customerEmail);
  },[booking])

  // Export booking details to PDF
  const exportToPDF = () => {
    if (!booking) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = 20;

    // Title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Booking Details", margin, yPosition);
    yPosition += 15;

    // Booking ID
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`Booking ID: ${booking.id || 'N/A'}`, margin, yPosition);
    yPosition += 10;

    // Line separator
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Resource Information
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Resource Information", margin, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Resource Name: ${booking.resource?.name || 'Unknown'}`, margin + 5, yPosition);
    yPosition += 8;
    doc.text(`Category: ${formatCategoryName(booking.resource?.metadata?.category) || 'N/A'}`, margin + 5, yPosition);
    yPosition += 8;
    doc.text(`Capacity: ${booking.resource?.metadata?.capacity || booking.resource?.max_simultaneous_bookings || 1}`, margin + 5, yPosition);
    yPosition += 15;

    // Customer Information
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Information", margin, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");

    // Determine customer name
    let email = booking.customer_id || booking.customer_name || booking.metadata?.customer_name;
    if (booking.customer_name && booking.customer_name.includes("@")) {
      email = booking.customer_name;
    }
    const customerName = email && userMap.has(email) ? userMap.get(email) : booking.metadata?.customer_name || booking.customer_name || "Unknown";

    doc.text(`Customer Name: ${customerName}`, margin + 5, yPosition);
    yPosition += 8;
    if (email) {
      doc.text(`Email: ${customerEmail || email}`, margin + 5, yPosition);
      yPosition += 8;
    }
    yPosition += 7;

    // Booking Details
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Booking Details", margin, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Date: ${formatDate(booking.starts_at)}`, margin + 5, yPosition);
    yPosition += 8;
    doc.text(`Start Time: ${formatTime(booking.starts_at)}`, margin + 5, yPosition);
    yPosition += 8;
    doc.text(`End Time: ${formatTime(booking.ends_at)}`, margin + 5, yPosition);
    yPosition += 8;
    doc.text(`Duration: ${calculateDuration(booking.starts_at, booking.ends_at)}`, margin + 5, yPosition);
    yPosition += 15;

    // Status Information
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Status", margin, yPosition);
    yPosition += 10;

    const status = getStatus(booking);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");

    // Set color based on status
    if (status === 'Confirmed') {
      doc.setTextColor(34, 197, 94); // Green
    } else if (status === 'Tentative') {
      doc.setTextColor(234, 179, 8); // Yellow
    } else if (status === 'Cancelled') {
      doc.setTextColor(239, 68, 68); // Red
    }

    doc.text(`Status: ${status}`, margin + 5, yPosition);
    doc.setTextColor(0, 0, 0); // Reset to black
    yPosition += 15;

    // Additional Information
    if (booking.notes || booking.metadata?.notes || booking.metadata?.special_requests) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Additional Information", margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      
      if (booking.notes) {
        const notesText = doc.splitTextToSize(`Notes: ${booking.notes}`, pageWidth - margin * 2 - 10);
        doc.text(notesText, margin + 5, yPosition);
        yPosition += (notesText.length * 6) + 4;
      } else if (booking.metadata?.notes) {
        const notesText = doc.splitTextToSize(`Notes: ${booking.metadata.notes}`, pageWidth - margin * 2 - 10);
        doc.text(notesText, margin + 5, yPosition);
        yPosition += (notesText.length * 6) + 4;
      }
      
      if (booking.metadata?.special_requests) {
        const requestsText = doc.splitTextToSize(`Special Requests: ${booking.metadata.special_requests}`, pageWidth - margin * 2 - 10);
        doc.text(requestsText, margin + 5, yPosition);
        yPosition += (requestsText.length * 6) + 4;
      }
    }

    // Footer
    yPosition = doc.internal.pageSize.getHeight() - 15;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleString()}`, margin, yPosition);

    // Save the PDF
    const fileName = `booking_${booking.id || 'details'}_${new Date().getTime()}.pdf`;
    doc.save(fileName);

    showToast('PDF exported successfully!', 'success');
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Add custom email
  const handleAddCustomEmail = () => {
    const trimmedEmail = customEmail.trim();
    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    if (selectedRecipients.includes(trimmedEmail)) {
      showToast('This email is already added', 'error');
      return;
    }

    setSelectedRecipients([...selectedRecipients, trimmedEmail]);
    setCustomEmail('');
  };

  const handleRemoveRecipient = (email) => {
    setSelectedRecipients(selectedRecipients.filter(e => e !== email));
  };


  useEffect(() => {
    const userMap = new Map();
    const storedUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (storedUser.email) {
      const username = storedUser.username || storedUser.name || storedUser.email.split('@')[0];
      userMap.set(storedUser.email, username);
    }
    setUserMap(userMap);
    fetchBookingDetails();
  }, [id]);

  useEffect(() => {
    if (booking) {
      setEditedStartTime(formatTime24(booking.starts_at));
      setEditedEndTime(formatTime24(booking.ends_at));
      setEditedDate(formatDateForInput(booking.starts_at));
      setEditedNotes(booking.notes || ""); 
    }
  }, [booking]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/bookings/viewBooking/${id}`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch booking details");

      const data = await response.json();
      let notesFromMongo = '';
      const mongoId = data.mongo_id || data._id || data.mongoId;
    
      if (mongoId) {
        try {
          const notesResponse = await fetch(`https://njs-01.optimuslab.space/booking_system/booking/${mongoId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAccessToken()}`,
            }
          });
          
          if (notesResponse.ok) {
            const mongoData = await notesResponse.json();
            notesFromMongo = mongoData.notes || '';
            data.notes = notesFromMongo; 
          }
        } catch (error) {
          console.error('Error fetching notes from MongoDB:', error);
        }
      }

      setBooking(data);
      console.log("Fetched booking details:", data);

      if (data.service) {
        setServiceDetails(data.service);
      }

      if (data.resource?.name) {
        fetchResourceSchedule(data.resource.name);
      }
    } catch (err) {
      setError(err.message || "Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const fetchResourceSchedule = async (resourceName) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/resource/getResourceScheduleInfo/${encodeURIComponent(resourceName)}`,
        {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`,
          },
        }
      );
  
      if (!response.ok) throw new Error("Failed to fetch resource schedule");
  
      const responseData = await response.json();
      const data = {
        resource: responseData.data.resource,
        recurring_schedule: responseData.data.recurring_schedule,
        schedule_blocks: responseData.data.schedule_blocks
      };
      setResourceSchedule(data);
    } catch (err) {
      console.error("Error fetching resource schedule:", err);
    }
  };

  const handleTimeChange = (field, value) => {
    if (field === "start") setEditedStartTime(value);
    else if (field === "end") setEditedEndTime(value);
    else if (field === "date") setEditedDate(value);
    setHasChanges(true);
  };

  // Fetch all bookings for this resource on this date for availability display
  useEffect(() => {
    const fetchAvailabilityBookings = async () => {
      if (!booking || !booking.resource?.id || !booking.starts_at) return;

      setLoadingAvailability(true);
      try {
        const bookingDate = new Date(booking.starts_at).toISOString().split('T')[0];

        const response = await fetch(
          `${API_BASE_URL}/bookings/viewFilteredBookings`,
          { 
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAccessToken()}`,
            },
            body: JSON.stringify({ resource_id: booking.resource.id })
          }
        );

        const data = await response.json();
        const dayBookings = (data.data || []).filter(b => {
          if (!b.starts_at || b.is_canceled) return false;
          const bDate = new Date(b.starts_at).toISOString().split('T')[0];
          return bDate === bookingDate;
        });

        setAvailabilityBookings(dayBookings);
      } catch (error) {
        setAvailabilityBookings([]);
      } finally {
        setLoadingAvailability(false);
      }
    };

    fetchAvailabilityBookings();
  }, [booking?.resource?.id, booking?.starts_at]);

  const handleDurationClick = (hours) => {
    if (!editedStartTime || !editedDate) return;

    const [startHours, startMinutes] = editedStartTime.split(":").map(Number);
    const endHours = startHours + hours;
    
    // Ensure end hours wrap around correctly to string format
    setEditedEndTime(
      `${String(endHours % 24).padStart(2, "0")}:${String(startMinutes).padStart(2, "0")}`
    );
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!booking || !hasChanges) return;

    if (isBookingInPast(booking)) {
      showToast("Cannot modify a booking that has already ended.", "error");
      return;
    }

    setActionLoading("save");

    try {
      const startDateTime = `${editedDate}T${editedStartTime}:00+07:00`;
      const endDateTime = `${editedDate}T${editedEndTime}:00+07:00`;

      const response = await fetch(`${API_BASE_URL}/bookings/updateBooking`, {
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ 
          booking_id: id,
          starts_at: startDateTime,
          ends_at: endDateTime
        }),
      });

      if (!response.ok) throw new Error("Failed to update booking");

      const mongoId = booking.mongo_id || booking._id || booking.mongoId;
      if (mongoId && editedNotes !== (booking.notes || "")) {
        try {
          await fetch(`https://njs-01.optimuslab.space/booking_system/booking/${mongoId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAccessToken()}`,
            },
            body: JSON.stringify({ notes: editedNotes.trim() }),
          });
        } catch (notesError) {}
      }

      await fetchBookingDetails();
      setHasChanges(false);
      showToast("Booking updated successfully", "success");
    } catch (error) {
      showToast(`Failed to update booking: ${error.message}`, "error");
    } finally {
      setActionLoading(null);
    }
  };

 const handleMarkTentative = async () => setShowMarkTentativeModal(true);

const confirmMarkTentative = async () => {
  if (!booking) return;
  if (isBookingInPast(booking)) return showToast("Cannot modify a past booking.", "error");

  setActionLoading("Mark Tentative");
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/updateBooking`, {
      method: "PATCH",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ booking_id: id, is_temporary: true }),
    });
    if (!response.ok) throw new Error("Failed to mark booking as tentative");

    await fetchBookingDetails();
    setShowMarkTentativeModal(false);
    showToast("Booking marked as tentative successfully", "success");
  } catch (error) {
    showToast(`Failed to mark booking as tentative: ${error.message}`, "error");
  } finally {
    setActionLoading(null);
  }
};

const handleConfirmBooking = async () => setShowConfirmBookingModal(true);

const confirmConfirmBooking = async () => {
  if (!booking) return;
  if (isBookingInPast(booking)) return showToast("Cannot modify a past booking.", "error");

  setActionLoading("Confirm");
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/updateBooking`, {
      method: "PATCH",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ booking_id: id, is_temporary: false }),
    });
    if (!response.ok) throw new Error("Failed to confirm booking");

    await fetchBookingDetails();
    setShowConfirmBookingModal(false);
    showToast("Booking confirmed successfully", "success");
  } catch (error) {
    showToast(`Failed to confirm booking: ${error.message}`, "error");
  } finally {
    setActionLoading(null);
  }
};

const handleCancelBooking = async () => setShowCancelModal(true);

const confirmCancelBooking = async () => {
  if (!booking) return;
  if (isBookingInPast(booking)) return showToast("Cannot cancel a past booking.", "error");

  setActionLoading("Cancel");
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/updateBooking`, {
      method: "PATCH",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ booking_id: id, is_canceled: true }),
    });
    if (!response.ok) throw new Error("Failed to cancel booking");

    await fetchBookingDetails();
    setShowCancelModal(false);
    showToast("Booking cancelled successfully", "success");
  } catch (error) {
    showToast(`Failed to cancel booking: ${error.message}`, "error");
  } finally {
    setActionLoading(null);
  }
};

const handleReactivateBooking = async (newStatus) => {
  if (newStatus === "Confirmed") setShowReactivateConfirmedModal(true);
  else setShowReactivateTentativeModal(true);
};

const confirmReactivateConfirmed = async () => {
  if (!booking) return;
  if (isBookingInPast(booking)) return showToast("Cannot modify a past booking.", "error");

  setActionLoading("Reactivate-Confirmed");
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/updateBooking`, {
      method: "PATCH",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ booking_id: id, is_canceled: false, is_temporary: false }),
    });
    if (!response.ok) throw new Error("Failed to reactivate booking");

    await fetchBookingDetails();
    setShowReactivateConfirmedModal(false);
    showToast("Booking reactivated as Confirmed successfully", "success");
  } catch (error) {
    showToast(`Failed to reactivate booking: ${error.message}`, "error");
  } finally {
    setActionLoading(null);
  }
};

const confirmReactivateTentative = async () => {
  if (!booking) return;
  if (isBookingInPast(booking)) return showToast("Cannot modify a past booking.", "error");

  setActionLoading("Reactivate-Tentative");
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/updateBooking`, {
      method: "PATCH",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ booking_id: id, is_canceled: false, is_temporary: true }),
    });
    if (!response.ok) throw new Error("Failed to reactivate booking");

    await fetchBookingDetails();
    setShowReactivateTentativeModal(false);
    showToast("Booking reactivated as Tentative successfully", "success");
  } catch (error) {
    showToast(`Failed to reactivate booking: ${error.message}`, "error");
  } finally {
    setActionLoading(null);
  }
};

// handle send email button

const handleSendEmail = async () => {
  if (!booking) return;

  setActionLoading("sendEmail");
  try {
    const response = await fetch(
      "https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings/sendBookingEmail",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({
          customer_id: booking.metadata?.customer_id,
          customer_name: booking.metadata?.customer_name,
          resource_name: booking.resource?.name,
          location_name: booking.location?.name,
          starts_at: booking.starts_at,
          ends_at: booking.ends_at,
          price: booking.price,
          photo_url: booking.resource?.metadata?.photo_url,
        }),
      }
    );

    if (!response.ok) throw new Error("Failed to send email");
    showToast("Booking email sent successfully!", "success");
  } catch (error) {
    showToast(`Failed to send email: ${error.message}`, "error");
  } finally {
    setActionLoading(null);
  }
};

  const handleBack = () => {
    navigate("/bookings");
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#FBFBFB" }}>
        <div className="flex h-screen overflow-hidden">
          <Sidebar currentPath="/bookings" />
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-y-auto p-6">
              <div className="max-w-6xl mx-auto">
                <button onClick={handleBack} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <div className="bg-white rounded-lg p-6" style={{ boxShadow: "0px 0.9px 4px rgba(0,0,0,0.08)", borderRadius: "6px" }}>
                  <BookingDetailsSkeleton />
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#FBFBFB" }}>
        <div className="flex h-screen overflow-hidden">
          <Sidebar currentPath="/bookings" />
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-y-auto p-6">
              <div className="max-w-6xl mx-auto">
                <button onClick={handleBack} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <div className="bg-white rounded-lg p-8 text-center">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load booking</h2>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <button onClick={handleBack} className="px-4 py-1.5 text-sm font-medium text-white rounded" style={{ backgroundColor: "#1A5090", height: "32px" }}>
                    Back to Bookings
                  </button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const status = getStatus(booking);
  const isPastBooking = isBookingInPast(booking);

  let email = booking.customer_id || booking.customer_name || booking.metadata?.customer_name;
  if (booking.customer_name && booking.customer_name.includes("@")) {
    email = booking.customer_name;
  } else if (booking.metadata?.customer_name && booking.metadata.customer_name.includes("@")) {
    email = booking.metadata.customer_name;
  }

  const customerName = email && userMap.has(email) ? userMap.get(email) : booking.metadata?.customer_name || booking.customer_name || "Unknown";
  const resourcePhotoUrl = booking.resource?.metadata?.photo_url;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBFBFB" }}>
      <div className="flex h-screen overflow-hidden">
        <Sidebar currentPath="/bookings" />
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto p-2 sm:p-6">
            <div className="max-w-6xl mx-auto">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-150"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              <div
                className="bg-white rounded-lg transition-all duration-220 ease-out p-2 sm:p-6"
                style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "6px",
                  boxShadow: "0px 0.9px 4px rgba(0,0,0,0.08)",
                }}
              >
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-16 rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden bg-[#D3DAE6]">
                      {resourcePhotoUrl ? (
                        <img src={resourcePhotoUrl} alt="Resource" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-400 rounded flex items-center justify-center text-gray-600 text-xs">Room</div>
                      )}
                    </div>
                    <div>
                      <h1 className="font-semibold mb-2 text-2xl text-black leading-tight">
                        {booking.resource?.name || "Unknown"}
                      </h1>
                      <p className="font-normal text-base text-[#666666] font-inter">
                        {formatShortDate(booking.starts_at)} at {formatTime24(booking.starts_at)} → {formatTime24(booking.ends_at)}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Users className="w-4 h-4" /> <span>x{booking.resource?.metadata?.capacity || 1}</span>
                        </div>
                        {booking.resource?.metadata?.category && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                            {formatCategoryName(booking.resource.metadata.category)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-left sm:text-right w-full sm:w-auto">
                      <div className="flex items-center sm:justify-end gap-2 text-gray-900 mb-2">
                      <User className="w-4 h-4" /> <span className="text-[16px] font-inter">{customerName}</span>
                    </div>
                  </div>
                </div>

                {/* Summary Section */}
                <div className="mb-6 p-4 rounded-lg bg-[#FAFAFA] border border-[#E5E7EB]">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-[16px] text-black font-inter">Summary</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={exportToPDF} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#1A5090] rounded-lg hover:bg-blue-800 shadow-sm">
                        <Download className="w-4 h-4" /> Export PDF
                      </button>
                      <button
                        onClick={handleSendEmail}
                        disabled={actionLoading === "sendEmail"}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#16A34A] rounded-lg hover:bg-green-700 shadow-sm disabled:opacity-50"
                      >
                        {actionLoading === "sendEmail" ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" /> Send Email
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="text-[14px] text-[#666666] font-inter">{booking.resource?.name || "Unknown"}</p>
                  <p className="text-[14px] text-[#666666] font-inter">
                    {formatShortDate(booking.starts_at)} at {formatTime24(booking.starts_at)} → {formatTime24(booking.ends_at)}
                  </p>
                  {isPastBooking && (
                    <div className="mt-3 p-3 rounded flex items-start gap-2 bg-[#FEF2F2] border border-[#FCA5A5]">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-[14px] text-[#DC2626] font-medium font-inter">You can't edit a booking in the past.</p>
                    </div>
                  )}
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-[142px]">
                  {/* Left Column - Main Details */}
                  <div className="col-span-2 space-y-6">
                    <div>
                      <h3 className="mb-2 text-[16px] font-medium text-black font-inter">Status</h3>
                      <span className="inline-flex px-3 py-1 text-sm font-medium rounded" style={{ ...getStatusStyle(status) }}>{status}</span>
                    </div>

                    {/* Editable Booking Time Section */}
                    <div>
                      <h3 className="mb-3 text-[16px] font-medium text-black font-inter">Booking Time</h3>
                      <div className="space-y-4">
                        
                        {/* Date Input */}
                        <div>
                          <label className="block text-sm font-medium mb-2 text-[#374151] font-inter">Date</label>
                          <input
                            type="date"
                            value={editedDate}
                            onChange={(e) => handleTimeChange("date", e.target.value)}
                            disabled={isPastBooking || booking.is_canceled}
                            className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-400 font-inter cursor-pointer"
                          />
                        </div>

                        {/* Start and End Time (Native Browser Pickers) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          
                          {/* Start Time */}
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium mb-2 text-[#374151] font-inter">
                              <Clock className="w-4 h-4" /> Start time *
                            </label>
                            <input
                              type="time"
                              value={editedStartTime}
                              onChange={(e) => {
                                const time = e.target.value;
                                handleTimeChange("start", time);
                                if (serviceDetails?.duration && time) {
                                  const durationMinutes = parseDuration(serviceDetails.duration);
                                  handleTimeChange("end", addMinutesToTime(time, durationMinutes));
                                }
                              }}
                              disabled={isPastBooking || booking.is_canceled}
                              className={`w-full px-4 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed font-inter cursor-pointer ${isPastBooking || booking.is_canceled ? 'bg-gray-50' : 'bg-white'}`}
                              required
                            />
                          </div>

                          {/* End Time */}
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium mb-2 text-[#374151] font-inter">
                              <Clock className="w-4 h-4" /> End time *
                            </label>
                            <input
                              type="time"
                              value={editedEndTime}
                              onChange={(e) => handleTimeChange("end", e.target.value)}
                              disabled={isPastBooking || booking.is_canceled}
                              className={`w-full px-4 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed font-inter cursor-pointer ${isPastBooking || booking.is_canceled ? 'bg-gray-50' : 'bg-white'}`}
                              required
                            />
                          </div>
                        </div>

                        {/* Duration Quick Select Buttons */}
                        {!isPastBooking && !booking.is_canceled && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            {[1, 2, 4, 8].map((hours) => (
                              <button
                                key={hours}
                                onClick={() => handleDurationClick(hours)}
                                className="px-4 py-2 text-sm rounded-lg transition-all hover:bg-blue-700 bg-[#1A5090] text-white font-inter"
                              >
                                {hours === 1 ? "1 hour" : `${hours} hours`}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Duration Display */}
                    <div>
                      <h3 className="mb-2 text-[16px] font-medium font-inter">Duration</h3>
                      <div className="inline-flex px-3 py-1 text-sm bg-white border border-[#B8B8B8] rounded shadow-sm font-inter">
                        {calculateDuration(`${editedDate}T${editedStartTime}`, `${editedDate}T${editedEndTime}`)}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-2">
                      <h3 className="mb-3 text-[16px] font-medium font-inter">Action</h3>
                      <div className="flex flex-wrap gap-3">
                        {booking.is_canceled ? (
                          <>
                            <button
                              onClick={() => handleReactivateBooking("Confirmed")}
                              disabled={actionLoading === "Reactivate-Confirmed" || isPastBooking}
                              className="px-4 py-1.5 text-sm font-medium border rounded transition-all hover:bg-green-50 disabled:opacity-50"
                              style={{ borderColor: "#16A34A", color: "#16A34A", backgroundColor: "#FFFFFF" }}
                            >
                              {actionLoading === "Reactivate-Confirmed" ? "Reactivating..." : "Reactivate as Confirmed"}
                            </button>
                            <button
                              onClick={() => handleReactivateBooking("Tentative")}
                              disabled={actionLoading === "Reactivate-Tentative" || isPastBooking}
                              className="px-4 py-1.5 text-sm font-medium border rounded transition-all hover:bg-yellow-50 disabled:opacity-50"
                              style={{ borderColor: "#EDB02D", color: "#EDB02D", backgroundColor: "#FFFFFF" }}
                            >
                              {actionLoading === "Reactivate-Tentative" ? "Reactivating..." : "Reactivate as Tentative"}
                            </button>
                          </>
                        ) : (
                          <>
                            {!booking.is_temporary ? (
                              <button
                                onClick={handleMarkTentative}
                                disabled={actionLoading === "Mark Tentative" || isPastBooking}
                                className="px-4 py-1.5 text-sm font-medium border rounded transition-all hover:bg-gray-50 disabled:opacity-50"
                                style={{ borderColor: "#6E6969", color: "#6E6969", backgroundColor: "#FFFFFF" }}
                              >
                                {actionLoading === "Mark Tentative" ? "Updating..." : "Mark Tentative"}
                              </button>
                            ) : (
                              <button
                                onClick={handleConfirmBooking}
                                disabled={actionLoading === "Confirm" || isPastBooking}
                                className="px-4 py-1.5 text-sm font-medium border rounded transition-all hover:bg-green-50 disabled:opacity-50"
                                style={{ borderColor: "#16A34A", color: "#16A34A", backgroundColor: "#FFFFFF" }}
                              >
                                {actionLoading === "Confirm" ? "Confirming..." : "Confirm Booking"}
                              </button>
                            )}
                            <button
                              onClick={handleCancelBooking}
                              disabled={actionLoading === "Cancel" || isPastBooking}
                              className="px-4 py-1.5 text-sm font-medium border rounded transition-all hover:bg-red-50 disabled:opacity-50"
                              style={{ borderColor: "#db6969", color: "#db6969", backgroundColor: "#FFFFFF" }}
                            >
                              {actionLoading === "Cancel" ? "Canceling..." : "Cancel Booking"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="mb-3 text-[16px] font-medium text-black font-inter">Notes / Remarks</h3>
                      <textarea
                        value={editedNotes}
                        onChange={(e) => {
                          setEditedNotes(e.target.value);
                          setHasChanges(true);
                        }}
                        disabled={isPastBooking || booking.is_canceled}
                        placeholder="Add any notes or remarks for this booking..."
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none min-h-[100px] font-inter"
                      />
                    </div>

                    {/* Save Button */}
                    {!isPastBooking && (
                      <div className="flex justify-end pt-2">
                        <button
                          onClick={handleSaveChanges}
                          disabled={!hasChanges || actionLoading === "save"}
                          className="px-6 py-2 text-sm font-medium text-white bg-[#184AC0] rounded hover:brightness-90 focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
                        >
                          {actionLoading === "save" ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Availability */}
                  <div>
                    <h3 className="mb-4 text-[16px] font-medium font-inter">Availability</h3>
                    <div className="bg-white border border-[#E5E7EB] rounded overflow-y-auto h-[400px] w-full lg:w-[250px]">
                      <style>{`
                        .unavailable-pattern {
                          background-image: repeating-linear-gradient(45deg, #9CA3AF 0, #9CA3AF 1px, transparent 0, transparent 50%);
                          background-size: 10px 10px;
                          opacity: 0.3;
                        }
                      `}</style>
                      <div className="p-3">
                        {loadingAvailability ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          </div>
                        ) : (() => {
                          const hkNow = getHongKongTime();
                          const currentHour = hkNow.getHours();
                          const currentMinute = hkNow.getMinutes();

                          const bookingDate = new Date(editedDate || booking.starts_at);
                          const bookingDateOnly = bookingDate.toISOString().split('T')[0];
                          const todayDate = hkNow.toISOString().split('T')[0];
                          const isToday = bookingDateOnly === todayDate;
                          const isFutureDate = bookingDateOnly > todayDate;

                          const bookingStartTimeStr = new Date(booking.starts_at).toLocaleTimeString('en-US', {
                            hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jakarta'
                          });
                          const [bookingStartHour, bookingStartMinute] = bookingStartTimeStr.split(':').map(Number);
                          const bookingWeekday = getWeekdayName(editedDate || booking.starts_at);
                          const scheduleBlocks = resourceSchedule?.schedule_blocks?.filter(block => block.weekday === bookingWeekday) || [];
                          
                          const interval = serviceDetails?.bookable_interval ? parseDuration(serviceDetails.bookable_interval) : 15;

                          const isInSchedule = (hour, minute) => {
                            if (!scheduleBlocks.length) return false;
                            const timeInMins = hour * 60 + minute;
                            return scheduleBlocks.some(b => {
                              const [sh, sm] = b.start_time.split(':').map(Number);
                              const [eh, em] = b.end_time.split(':').map(Number);
                              return timeInMins >= (sh * 60 + sm) && timeInMins < (eh * 60 + em);
                            });
                          };

                          const isSlotBooked = (hour, minute) => {
                            const slotTime = hour * 60 + minute;
                            return availabilityBookings.some(b => {
                              if (b.id === booking.id) return false; // exclude current booking
                              const startTimeStr = new Date(b.starts_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jakarta' });
                              const endTimeStr = new Date(b.ends_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jakarta' });
                              const [sH, sM] = startTimeStr.split(':').map(Number);
                              const [eH, eM] = endTimeStr.split(':').map(Number);
                              return slotTime >= (sH * 60 + sM) && slotTime < (eH * 60 + eM);
                            });
                          };

                          const isCurrentBookingSlot = (hour, minute) => {
                            const slotTime = hour * 60 + minute;
                            const [sh, sm] = editedStartTime.split(':').map(Number);
                            const [eh, em] = editedEndTime.split(':').map(Number);
                            return slotTime >= (sh * 60 + sm) && slotTime < (eh * 60 + em);
                          };

                          const timeSlotsUI = [];
                          let startHour = 0, startMinute = 0;

                          if (isToday) {
                            startHour = currentHour;
                            startMinute = Math.floor(currentMinute / interval) * interval;
                          } else if (isFutureDate) {
                            let schedStart = 24 * 60;
                            if (scheduleBlocks.length > 0) {
                              const firstBlock = scheduleBlocks.reduce((earliest, block) => {
                                const [h, m] = block.start_time.split(':').map(Number);
                                const [eH, eM] = earliest.start_time.split(':').map(Number);
                                return (h * 60 + m) < (eH * 60 + eM) ? block : earliest;
                              });
                              const [h, m] = firstBlock.start_time.split(':').map(Number);
                              schedStart = h * 60 + m;
                            }
                            const earliestTime = Math.min(schedStart, (bookingStartHour * 60 + bookingStartMinute) - 60);
                            startHour = Math.max(0, Math.floor(earliestTime / 60));
                            startMinute = Math.floor((earliestTime % 60) / interval) * interval;
                          } else {
                            startHour = bookingStartHour;
                            startMinute = Math.floor(bookingStartMinute / interval) * interval;
                          }

                          for (let tMins = startHour * 60 + startMinute; tMins < 24 * 60; tMins += interval) {
                            const h = Math.floor(tMins / 60);
                            const m = tMins % 60;
                            const timeLabel = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                            const inSched = isInSchedule(h, m);
                            const booked = isSlotBooked(h, m);
                            const isCurrent = isCurrentBookingSlot(h, m);

                            timeSlotsUI.push(
                              <div key={`${h}-${m}`} className="flex items-center gap-3 mb-1">
                                <span className="text-left text-xs text-[#6B7280] font-medium w-[40px] flex-shrink-0">{timeLabel}</span>
                                <div className={`flex-1 h-[24px] rounded-sm flex items-center px-1 ${!inSched ? 'unavailable-pattern bg-gray-300' : isCurrent ? 'bg-[#1A5090]' : booked ? 'bg-[#db6969]' : 'bg-[#F3F4F6] border border-[#E5E7EB]'}`}>
                                  {isCurrent && <span className="text-white font-medium text-[9px]">Selected</span>}
                                  {booked && !isCurrent && <span className="text-white font-medium text-[9px]">Booked</span>}
                                </div>
                              </div>
                            );
                          }
                          return timeSlotsUI;
                        })()}
                      </div>
                    </div>

                    <div className="mt-3 space-y-1 text-xs text-gray-500">
                      <div className="flex items-center gap-2"><div className="w-4 h-4 bg-[#1A5090] rounded-sm"></div><span>Your booking</span></div>
                      <div className="flex items-center gap-2"><div className="w-4 h-4 bg-[#db6969] rounded-sm"></div><span>Other bookings</span></div>
                      <div className="flex items-center gap-2"><div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded-sm"></div><span>Available</span></div>
                      <div className="flex items-center gap-2"><div className="w-4 h-4 unavailable-pattern bg-gray-400 rounded-sm"></div><span>Outside schedule</span></div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
            
            {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

            <ConfirmModal
              confirmColor="#db6969" 
              isOpen={showCancelModal}
              onClose={() => setShowCancelModal(false)}
              onConfirm={confirmCancelBooking}
              title="Cancel Booking"
              message="Are you sure you want to cancel this booking? This action will mark the booking as cancelled."
              confirmText="Yes, Cancel Booking"
              cancelText="No, Keep Booking"
              isLoading={actionLoading === "Cancel"}
            />
            <ConfirmModal
              confirmColor="#EDB02D"
              isOpen={showMarkTentativeModal}
              onClose={() => setShowMarkTentativeModal(false)}
              onConfirm={confirmMarkTentative}
              title="Mark as Tentative"
              message="Are you sure you want to mark this booking as tentative?"
              confirmText="Yes, Mark as Tentative"
              cancelText="Cancel"
              isLoading={actionLoading === "Mark Tentative"}
            />
            <ConfirmModal
              confirmColor="#16A34A"
              isOpen={showConfirmBookingModal}
              onClose={() => setShowConfirmBookingModal(false)}
              onConfirm={confirmConfirmBooking}
              title="Confirm Booking"
              message="Are you sure you want to confirm this booking?"
              confirmText="Yes, Confirm Booking"
              cancelText="Cancel"
              isLoading={actionLoading === "Confirm"}
            />
            <ConfirmModal
              confirmColor="#16A34A"
              isOpen={showReactivateConfirmedModal}
              onClose={() => setShowReactivateConfirmedModal(false)}
              onConfirm={confirmReactivateConfirmed}
              title="Reactivate as Confirmed"
              message="Are you sure you want to reactivate this cancelled booking as Confirmed?"
              confirmText="Yes, Reactivate"
              cancelText="Cancel"
              isLoading={actionLoading === "Reactivate-Confirmed"}
            />
            <ConfirmModal
              confirmColor="#EDB02D"
              isOpen={showReactivateTentativeModal}
              onClose={() => setShowReactivateTentativeModal(false)}
              onConfirm={confirmReactivateTentative}
              title="Reactivate as Tentative"
              message="Are you sure you want to reactivate this cancelled booking as Tentative?"
              confirmText="Yes, Reactivate"
              cancelText="Cancel"
              isLoading={actionLoading === "Reactivate-Tentative"}
            />

          </main>
        </div>
      </div>
    </div>
  );
}